import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {TextComponent} cartShipping - The cart shipping estimate.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    this.#updateShipping();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty(
      "--row-height",
      `${cartItemRowToRemove.clientHeight}px`,
    );
    cartItemRowToRemove.classList.add("removing");

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();
    this.refs.cartShipping?.shimmer();
    /** @type {TextComponent | null} */ (
      this.querySelector(".at-free-shipping-progress text-component")
    )?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html",
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          }),
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
        );

        // Wywołaj po morphSection — inaczej morphdom nadpisuje wartości powrotem do "—"
        this.#updateShipping(
          parsedResponseText.total_weight,
          parsedResponseText.total_price,
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      // cart-discount.js calls morphSection synchronously after dispatching this event,
      // so we defer #updateShipping to run after that morphSection completes.
      const resource =
        /** @type {{ total_weight?: number, total_price?: number }} */ (
          event.detail.resource
        );
      setTimeout(
        () => this.#updateShipping(resource.total_weight, resource.total_price),
        0,
      );
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const { totalWeight, totalPrice } =
        this.#extractShippingDataFromSection(cartItemsHtml);
      morphSection(this.sectionId, cartItemsHtml);
      // Wywołaj po morphSection — inaczej morphdom nadpisuje wartości powrotem do "—"
      setTimeout(() => this.#updateShipping(totalWeight, totalPrice), 0);
    } else {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false })
        .then(() => {
          setTimeout(() => this.#updateShipping(), 0);
        });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Reads shipping totals from a section rendering response.
   * @param {string} html
   * @returns {{ totalWeight?: number, totalPrice?: number }}
   */
  #extractShippingDataFromSection(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const ratesEl = doc.querySelector("[data-shipping-rates]");
    if (!ratesEl) return {};

    const totalWeight = ratesEl.getAttribute("data-total-weight");
    const totalPrice = ratesEl.getAttribute("data-total-price");
    const parsedWeight =
      totalWeight != null ? parseInt(totalWeight, 10) : Number.NaN;
    const parsedPrice =
      totalPrice != null ? parseInt(totalPrice, 10) : Number.NaN;

    return {
      totalWeight: Number.isFinite(parsedWeight) ? parsedWeight : undefined,
      totalPrice: Number.isFinite(parsedPrice) ? parsedPrice : undefined,
    };
  }

  /**
   * Updates the shipping estimate value.
   * @param {number} [totalWeight] - Total weight in grams from cart API response.
   * @param {number} [totalPrice] - Total price in cents from cart API response.
   */
  #updateShipping(totalWeight, totalPrice) {
    const ratesEl = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-shipping-rates]")
    );
    if (!ratesEl) return;

    const cartShipping = ratesEl.querySelector("text-component");
    if (!cartShipping) return;

    if (totalPrice == null) {
      totalPrice = parseInt(ratesEl.dataset.totalPrice || "0", 10) || 0;
    }

    let threshold = 0;
    const freeShippingBar = /** @type {HTMLElement | null} */ (
      this.querySelector(".at-free-shipping-progress")
    );
    if (freeShippingBar) {
      threshold = parseInt(freeShippingBar.dataset.threshold || "0", 10);
      if (threshold === 0 || totalPrice >= threshold) {
        const formatted = "GRATIS";
        cartShipping.textContent = formatted;
        cartShipping.setAttribute("value", formatted);
        cartShipping.removeAttribute("shimmer");
        return;
      }
    }

    if (totalWeight == null) {
      totalWeight = parseInt(ratesEl.dataset.totalWeight || "0", 10) || 0;
    }

    const parsed = JSON.parse(ratesEl.dataset.shippingRates || "[]");
    const rates = Array.isArray(parsed) ? parsed : [parsed];
    rates.sort((a, b) => {
      const aHasMax = a.max_weight && a.max_weight.value != null ? 0 : 1;
      const bHasMax = b.max_weight && b.max_weight.value != null ? 0 : 1;
      return aHasMax - bHasMax;
    });
    const matched = rates.find((r) => {
      const minG = this.#weightToGrams(r.min_weight);
      const maxG = this.#weightToGrams(r.max_weight);
      return totalWeight >= minG && totalWeight <= maxG;
    });

    if (matched) {
      const amount = parseFloat(matched.price.amount);
      // Show GRATIS from rate only when there is no threshold (threshold === 0).
      // If threshold > 0 and cart hasn't reached it yet (we're past the early return above),
      // a rate with amount 0 means "free if threshold reached" — don't show GRATIS prematurely.
      const formatted =
        amount === 0 && threshold === 0
          ? "GRATIS"
          : `${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${matched.price.currency_code}`;
      cartShipping.textContent = formatted;
      cartShipping.setAttribute("value", formatted);
    } else {
      cartShipping.textContent = "—";
      cartShipping.setAttribute("value", "—");
    }

    cartShipping.removeAttribute("shimmer");
  }

  /**
   * Converts a weight object to grams.
   * @param {{ value: number, unit: string } | null} weight
   * @returns {number}
   */
  #weightToGrams(weight) {
    if (!weight || weight.value == null) return Infinity;
    return weight.unit === "kg" ? weight.value * 1000 : weight.value;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
